import { useState, useEffect, useCallback } from "react";
import AdminLayout from "../components/AdminLayout";
import { getSupabaseClient } from "../../../lib/supabaseClient";
import type { Itinerary, ItineraryStatus, Order, Photographer } from "../../../store/types";
import { adaptItineraryFromApi } from "../../../api/adapters";
import { createItineraryStatusNotice } from "../../../api/itineraryRelayApi";

// ─── Types ────────────────────────────────────────────────────────────────────

interface EnrichedItinerary extends Itinerary {
  order_number: string;
  selected_city: string;
  shoot_date: string;
  photographer_name: string;
}

interface FormState {
  order_id: string;
  photographer_id: string;
  meeting_point: string;
  meeting_time: string;
  map_url: string;
  notes: string;
  internal_notes: string;
  status: ItineraryStatus;
}

const EMPTY_FORM: FormState = {
  order_id: "",
  photographer_id: "",
  meeting_point: "",
  meeting_time: "",
  map_url: "",
  notes: "",
  internal_notes: "",
  status: "draft",
};

// ─── Status config ────────────────────────────────────────────────────────────

const STATUS_CONFIG: Record<ItineraryStatus, { label: string; cls: string; dot: string }> = {
  draft: { label: "草稿", cls: "bg-[#F0EDEA] text-[#7A6B5A]", dot: "bg-[#C8B8A6]" },
  confirmed: { label: "已確認", cls: "bg-emerald-50 text-emerald-700", dot: "bg-emerald-500" },
  updated: { label: "已更新", cls: "bg-teal-50 text-teal-700", dot: "bg-teal-500" },
  cancelled: { label: "已取消", cls: "bg-red-50 text-red-600", dot: "bg-red-400" },
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatLocalTime(iso: string | null): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("zh-TW", {
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

// ─── StatusBadge ─────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: ItineraryStatus }) {
  const cfg = STATUS_CONFIG[status] ?? STATUS_CONFIG.draft;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-light ${cfg.cls}`}>
      <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${cfg.dot}`} />
      {cfg.label}
    </span>
  );
}

// ─── RelayNoticeTag ───────────────────────────────────────────────────────────

function RelayNoticeTag() {
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-light bg-teal-50 text-teal-600 border border-teal-100 whitespace-nowrap">
      <div className="w-3 h-3 flex items-center justify-center">
        <i className="ri-notification-3-line text-[10px]" />
      </div>
      Relay 通知已發送
    </span>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function AdminItinerariesPage() {
  const [itineraries, setItineraries] = useState<EnrichedItinerary[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [photographers, setPhotographers] = useState<Photographer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<Itinerary | null>(null);
  const [prevStatus, setPrevStatus] = useState<ItineraryStatus>("draft");
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [relaySuccess, setRelaySuccess] = useState<string | null>(null);

  const [deleteId, setDeleteId] = useState<string | null>(null);

  const supabase = getSupabaseClient();

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [itinRes, ordersRes, phRes] = await Promise.all([
        supabase
          .from("itineraries")
          .select("*")
          .order("created_at", { ascending: false }),
        supabase
          .from("orders")
          .select("id,order_number,selected_city,shoot_date,customer_name")
          .order("shoot_date", { ascending: false }),
        supabase
          .from("photographers")
          .select("id,display_name")
          .eq("is_active", true)
          .order("display_name"),
      ]);

      if (itinRes.error) throw itinRes.error;
      if (ordersRes.error) throw ordersRes.error;

      const orderMap = new Map(
        ((ordersRes.data ?? []) as { id: string; order_number: string; selected_city: string; shoot_date: string; customer_name: string }[]).map((o) => [o.id, o])
      );
      const phMap = new Map(
        ((phRes.data ?? []) as { id: string; display_name: string }[]).map((p) => [p.id, p.display_name])
      );

      const enriched: EnrichedItinerary[] = (itinRes.data ?? []).map((raw: unknown) => {
        const it = adaptItineraryFromApi(raw);
        const o = orderMap.get(it.order_id);
        return {
          ...it,
          order_number: o?.order_number ?? "—",
          selected_city: o?.selected_city ?? "—",
          shoot_date: o?.shoot_date ?? "—",
          photographer_name: it.photographer_id ? (phMap.get(it.photographer_id) ?? "—") : "—",
        };
      });

      setItineraries(enriched);
      setOrders((ordersRes.data ?? []) as Order[]);
      setPhotographers((phRes.data ?? []) as Photographer[]);
    } catch (e) {
      setError(e instanceof Error ? e.message : "載入失敗");
    } finally {
      setLoading(false);
    }
  }, [supabase]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const set = (k: keyof FormState, v: string) => setForm((f) => ({ ...f, [k]: v }));

  const openCreate = () => {
    setEditTarget(null);
    setPrevStatus("draft");
    setForm(EMPTY_FORM);
    setRelaySuccess(null);
    setModalOpen(true);
  };

  const openEdit = (it: EnrichedItinerary) => {
    setEditTarget(it);
    setPrevStatus(it.status);
    setRelaySuccess(null);
    setForm({
      order_id: it.order_id,
      photographer_id: it.photographer_id ?? "",
      meeting_point: it.meeting_point,
      meeting_time: it.meeting_time ? it.meeting_time.slice(0, 16) : "",
      map_url: it.map_url,
      notes: it.notes,
      internal_notes: it.internal_notes,
      status: it.status,
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.order_id || !form.meeting_point) return;
    setSaving(true);
    setRelaySuccess(null);
    try {
      const payload = {
        order_id: form.order_id,
        photographer_id: form.photographer_id || null,
        meeting_point: form.meeting_point,
        meeting_time: form.meeting_time ? new Date(form.meeting_time).toISOString() : null,
        map_url: form.map_url,
        notes: form.notes,
        internal_notes: form.internal_notes,
        status: form.status,
        updated_at: new Date().toISOString(),
      };

      if (editTarget) {
        const { error } = await supabase.from("itineraries").update(payload).eq("id", editTarget.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("itineraries").insert(payload);
        if (error) throw error;
      }

      // Trigger relay notice when status is confirmed or updated (and status changed)
      const statusChanged = editTarget === null || prevStatus !== form.status;
      const shouldNotify =
        (form.status === "confirmed" || form.status === "updated") && statusChanged;

      if (shouldNotify) {
        const order = orders.find((o) => o.id === form.order_id);
        if (order) {
          const result = await createItineraryStatusNotice(
            form.order_id,
            form.status as "confirmed" | "updated",
            order.selected_city,
            order.shoot_date
          );
          if (result.success) {
            setRelaySuccess(`Relay 通知已發送 — ${order.selected_city} · ${order.shoot_date}`);
          }
        }
      }

      setModalOpen(false);
      await fetchData();
    } catch (e) {
      alert(e instanceof Error ? e.message : "儲存失敗");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    const { error } = await supabase.from("itineraries").delete().eq("id", id);
    if (!error) { setDeleteId(null); fetchData(); }
    else alert(error.message);
  };

  // ── Status → relay notification hint
  const willTriggerRelay =
    (form.status === "confirmed" || form.status === "updated") &&
    (editTarget === null || prevStatus !== form.status);

  return (
    <AdminLayout
      title="行程管理"
      subtitle="管理拍攝集合地點、時間與地圖連結"
      action={
        <button
          onClick={openCreate}
          className="flex items-center gap-1.5 px-4 py-2 bg-[#2C2C2C] text-white text-sm font-light rounded-lg cursor-pointer whitespace-nowrap hover:bg-[#3C3C3C] transition-colors"
        >
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-add-line" />
          </div>
          新增行程
        </button>
      }
    >
      {/* Relay success toast */}
      {relaySuccess && (
        <div className="mb-4 flex items-center gap-2 px-4 py-3 bg-teal-50 border border-teal-200/60 rounded-lg text-sm font-light text-teal-700">
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-checkbox-circle-line" />
          </div>
          {relaySuccess}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-64 text-[#2C2C2C]/40 text-sm font-light">
          載入中...
        </div>
      ) : error ? (
        <div className="flex flex-col items-center justify-center h-64 gap-3">
          <p className="text-sm text-red-500 font-light">{error}</p>
          <button onClick={fetchData} className="text-sm text-[#2C2C2C]/60 underline cursor-pointer">
            重試
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-[#D9D9D9]/60 overflow-x-auto">
          <table className="w-full text-sm min-w-[860px]">
            <thead>
              <tr className="border-b border-[#D9D9D9]/60">
                {["訂單編號", "城市", "拍攝日期", "攝影師", "集合地點", "集合時間", "狀態", "操作"].map((h) => (
                  <th
                    key={h}
                    className="text-left px-4 py-3 text-xs font-light text-[#2C2C2C]/50 whitespace-nowrap"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {itineraries.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-14 text-center text-sm text-[#2C2C2C]/30 font-light">
                    尚無行程資料
                  </td>
                </tr>
              ) : (
                itineraries.map((it) => (
                  <tr
                    key={it.id}
                    className="border-b border-[#D9D9D9]/30 hover:bg-[#F7F6F3]/60 transition-colors"
                  >
                    <td className="px-4 py-3 font-light text-[#2C2C2C]/80 whitespace-nowrap">
                      {it.order_number}
                    </td>
                    <td className="px-4 py-3 font-light text-[#2C2C2C]/60">{it.selected_city}</td>
                    <td className="px-4 py-3 font-light text-[#2C2C2C]/60 whitespace-nowrap">
                      {it.shoot_date}
                    </td>
                    <td className="px-4 py-3 font-light text-[#2C2C2C]/60 whitespace-nowrap">
                      {it.photographer_name}
                    </td>
                    <td className="px-4 py-3 font-light text-[#2C2C2C]/80 max-w-[180px] truncate">
                      {it.meeting_point || "—"}
                    </td>
                    <td className="px-4 py-3 font-light text-[#2C2C2C]/60 whitespace-nowrap">
                      {formatLocalTime(it.meeting_time)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <StatusBadge status={it.status} />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {it.map_url && (
                          <a
                            href={it.map_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-7 h-7 flex items-center justify-center text-[#C8B8A6] hover:text-[#2C2C2C] transition-colors cursor-pointer rounded"
                            title="開啟地圖"
                          >
                            <i className="ri-map-pin-2-line text-sm" />
                          </a>
                        )}
                        <button
                          onClick={() => openEdit(it)}
                          className="w-7 h-7 flex items-center justify-center text-[#2C2C2C]/40 hover:text-[#2C2C2C] transition-colors cursor-pointer rounded"
                          title="編輯"
                        >
                          <i className="ri-edit-line text-sm" />
                        </button>
                        <button
                          onClick={() => setDeleteId(it.id)}
                          className="w-7 h-7 flex items-center justify-center text-[#2C2C2C]/40 hover:text-red-500 transition-colors cursor-pointer rounded"
                          title="刪除"
                        >
                          <i className="ri-delete-bin-line text-sm" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Create / Edit Modal ─────────────────────────────────────────────── */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-lg shadow-lg max-h-[92vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-[#D9D9D9]/60 flex items-center justify-between">
              <h2 className="text-base font-light text-[#2C2C2C] tracking-wide">
                {editTarget ? "編輯行程" : "新增行程"}
              </h2>
              {willTriggerRelay && (
                <RelayNoticeTag />
              )}
            </div>

            <div className="px-6 py-5 flex flex-col gap-4">
              {/* Order selector — only when creating */}
              {!editTarget && (
                <div>
                  <label className="block text-xs font-light text-[#2C2C2C]/60 mb-1">
                    關聯訂單 <span className="text-red-400">*</span>
                  </label>
                  <select
                    value={form.order_id}
                    onChange={(e) => set("order_id", e.target.value)}
                    className="w-full border border-[#D9D9D9] rounded-lg px-3 py-2 text-sm font-light text-[#2C2C2C] focus:outline-none focus:border-[#C8B8A6] cursor-pointer"
                  >
                    <option value="">請選擇訂單</option>
                    {orders.map((o) => (
                      <option key={o.id} value={o.id}>
                        {o.order_number} — {o.selected_city} {o.shoot_date}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Status */}
              <div>
                <label className="block text-xs font-light text-[#2C2C2C]/60 mb-1">狀態</label>
                <select
                  value={form.status}
                  onChange={(e) => set("status", e.target.value as ItineraryStatus)}
                  className="w-full border border-[#D9D9D9] rounded-lg px-3 py-2 text-sm font-light text-[#2C2C2C] focus:outline-none focus:border-[#C8B8A6] cursor-pointer"
                >
                  {(Object.keys(STATUS_CONFIG) as ItineraryStatus[]).map((s) => (
                    <option key={s} value={s}>
                      {STATUS_CONFIG[s].label}
                    </option>
                  ))}
                </select>
                {willTriggerRelay && (
                  <p className="mt-1.5 flex items-center gap-1 text-[11px] font-light text-teal-600">
                    <i className="ri-notification-3-line" />
                    儲存後將自動發送 Relay 系統通知給客戶與攝影師
                  </p>
                )}
              </div>

              {/* Photographer selector */}
              <div>
                <label className="block text-xs font-light text-[#2C2C2C]/60 mb-1">指派攝影師</label>
                <select
                  value={form.photographer_id}
                  onChange={(e) => set("photographer_id", e.target.value)}
                  className="w-full border border-[#D9D9D9] rounded-lg px-3 py-2 text-sm font-light text-[#2C2C2C] focus:outline-none focus:border-[#C8B8A6] cursor-pointer"
                >
                  <option value="">— 未指派 —</option>
                  {photographers.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.display_name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Meeting point */}
              <div>
                <label className="block text-xs font-light text-[#2C2C2C]/60 mb-1">
                  集合地點 <span className="text-red-400">*</span>
                </label>
                <input
                  value={form.meeting_point}
                  onChange={(e) => set("meeting_point", e.target.value)}
                  placeholder="例：京都駅 中央口 前方廣場"
                  className="w-full border border-[#D9D9D9] rounded-lg px-3 py-2 text-sm font-light focus:outline-none focus:border-[#C8B8A6]"
                />
              </div>

              {/* Meeting time */}
              <div>
                <label className="block text-xs font-light text-[#2C2C2C]/60 mb-1">集合時間</label>
                <input
                  type="datetime-local"
                  value={form.meeting_time}
                  onChange={(e) => set("meeting_time", e.target.value)}
                  className="w-full border border-[#D9D9D9] rounded-lg px-3 py-2 text-sm font-light focus:outline-none focus:border-[#C8B8A6]"
                />
              </div>

              {/* Map URL */}
              <div>
                <label className="block text-xs font-light text-[#2C2C2C]/60 mb-1">地圖連結</label>
                <input
                  value={form.map_url}
                  onChange={(e) => set("map_url", e.target.value)}
                  placeholder="https://maps.google.com/..."
                  className="w-full border border-[#D9D9D9] rounded-lg px-3 py-2 text-sm font-light focus:outline-none focus:border-[#C8B8A6]"
                />
                {form.map_url && (
                  <a
                    href={form.map_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-1 inline-flex items-center gap-1 text-[11px] text-[#C8B8A6] hover:underline cursor-pointer"
                  >
                    <i className="ri-external-link-line" /> 預覽地圖連結
                  </a>
                )}
              </div>

              {/* Shoot notes — customer & photographer visible */}
              <div>
                <label className="block text-xs font-light text-[#2C2C2C]/60 mb-1">
                  拍攝說明
                  <span className="ml-1.5 px-1.5 py-0.5 bg-emerald-50 text-emerald-600 rounded text-[10px]">
                    客戶與攝影師可見
                  </span>
                </label>
                <textarea
                  value={form.notes}
                  onChange={(e) => set("notes", e.target.value)}
                  rows={3}
                  maxLength={500}
                  placeholder="拍攝路線建議、特別說明、穿著建議..."
                  className="w-full border border-[#D9D9D9] rounded-lg px-3 py-2 text-sm font-light focus:outline-none focus:border-[#C8B8A6] resize-none"
                />
              </div>

              {/* Internal notes — admin only */}
              <div>
                <label className="block text-xs font-light text-[#2C2C2C]/60 mb-1">
                  內部備註
                  <span className="ml-1.5 px-1.5 py-0.5 bg-amber-50 text-amber-600 rounded text-[10px]">
                    僅限 Admin 查看
                  </span>
                </label>
                <textarea
                  value={form.internal_notes}
                  onChange={(e) => set("internal_notes", e.target.value)}
                  rows={2}
                  maxLength={500}
                  placeholder="例：需帶備用設備 / 客戶有特殊需求..."
                  className="w-full border border-amber-100 rounded-lg px-3 py-2 text-sm font-light focus:outline-none focus:border-amber-300 resize-none bg-amber-50/30"
                />
              </div>
            </div>

            <div className="px-6 py-4 border-t border-[#D9D9D9]/60 flex justify-end gap-2">
              <button
                onClick={() => setModalOpen(false)}
                disabled={saving}
                className="px-4 py-2 text-sm font-light text-[#2C2C2C]/60 hover:text-[#2C2C2C] transition-colors cursor-pointer whitespace-nowrap"
              >
                取消
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !form.order_id || !form.meeting_point}
                className="px-4 py-2 bg-[#2C2C2C] text-white text-sm font-light rounded-lg cursor-pointer whitespace-nowrap hover:bg-[#3C3C3C] transition-colors disabled:opacity-40"
              >
                {saving ? "儲存中..." : "儲存"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete confirm ──────────────────────────────────────────────────── */}
      {deleteId && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-sm p-6">
            <h3 className="text-base font-light text-[#2C2C2C] mb-2">確認刪除此行程？</h3>
            <p className="text-sm text-[#2C2C2C]/50 font-light mb-5">此操作無法復原。</p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setDeleteId(null)}
                className="px-4 py-2 text-sm font-light text-[#2C2C2C]/60 cursor-pointer whitespace-nowrap"
              >
                取消
              </button>
              <button
                onClick={() => handleDelete(deleteId)}
                className="px-4 py-2 bg-red-500 text-white text-sm font-light rounded-lg cursor-pointer whitespace-nowrap hover:bg-red-600 transition-colors"
              >
                刪除
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
